import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import AdminAuth from '../components/AdminAuth';
import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  IconButton,
  useToast,
  Badge,
  Link,
  Flex,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useBreakpointValue,
} from '@chakra-ui/react';
import { DeleteIcon, ExternalLinkIcon, AddIcon, EditIcon } from '@chakra-ui/icons';
import { listGalleries, deleteGallery, getGalleryPurchases } from '../lib/galleryService';
import type { DatabaseGallery } from '../lib/supabase';

const AdminGalleries: React.FC = () => {
  const [galleries, setGalleries] = useState<DatabaseGallery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [galleryPurchases, setGalleryPurchases] = useState<{ [galleryId: string]: any[] }>({});
  const [, setLoadingPurchases] = useState<{ [galleryId: string]: boolean }>({});
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [galleryToDelete, setGalleryToDelete] = useState<DatabaseGallery | null>(null);

  // Responsive table display
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    loadGalleries();
  }, []);

  const loadGalleries = async () => {
    try {
      setLoading(true);
      const data = await listGalleries();
      setGalleries(data);
      
      // Load purchases for each gallery
      const purchasePromises = data.map(async (gallery) => {
        setLoadingPurchases(prev => ({ ...prev, [gallery.id]: true }));
        try {
          const result = await getGalleryPurchases(gallery.id);
          if (result.success && result.purchases) {
            setGalleryPurchases(prev => ({ ...prev, [gallery.id]: result.purchases || [] }));
          }
        } catch (error) {
          console.error(`Failed to load purchases for gallery ${gallery.id}:`, error);
        } finally {
          setLoadingPurchases(prev => ({ ...prev, [gallery.id]: false }));
        }
      });
      
      await Promise.all(purchasePromises);
    } catch (err) {
      setError('Failed to load galleries');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (gallery: DatabaseGallery) => {
    setGalleryToDelete(gallery);
    onOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!galleryToDelete) return;

    setDeletingId(galleryToDelete.id);
    try {
      const success = await deleteGallery(galleryToDelete.id);
      if (success) {
        toast({
          title: 'Gallery deleted',
          description: `"${galleryToDelete.title}" has been deleted successfully.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        await loadGalleries(); // Refresh the list
      } else {
        throw new Error('Failed to delete gallery');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete gallery. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeletingId(null);
      setGalleryToDelete(null);
      onClose();
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getGalleryStatus = (gallery: DatabaseGallery) => {
    if (!gallery.is_active) {
      return <Badge colorScheme="red">Inactive</Badge>;
    }
    
    if (gallery.expires_at) {
      const now = new Date();
      const expiryDate = new Date(gallery.expires_at);
      if (expiryDate < now) {
        return <Badge colorScheme="red">Expired</Badge>;
      }
      
      // Show warning if expiring within 7 days
      const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiry <= 7) {
        return <Badge colorScheme="orange">Expires in {daysUntilExpiry} days</Badge>;
      }
    }
    
    return <Badge colorScheme="green">Active</Badge>;
  };

  const getClientGalleryUrl = (token: string) => {
    return `${window.location.origin}/client/${token}`;
  };


  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minH="50vh">
          <Spinner size="xl" />
        </Box>
      );
    }

    if (error) {
      return (
        <Box maxW="container.xl" mx="auto" p={8}>
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        </Box>
      );
    }

    if (isMobile) {
    // Mobile card layout
    return (
      <Box maxW="container.xl" mx="auto" p={4} paddingTop={{ base: "80px", md: "20px" }}>
        <VStack spacing={6} align="stretch">
          <Flex justify="space-between" align="center">
            <VStack align="start" spacing={1}>
              <Text fontSize="2xl" fontWeight="bold">
                Admin Galleries ({galleries.length})
              </Text>
              <Text fontSize="sm" color="gray.600">
                <RouterLink to="/admin" style={{ color: 'inherit' }}>← Back to Dashboard</RouterLink>
              </Text>
            </VStack>
            <Button
              as={RouterLink}
              to="/admin/gallery"
              colorScheme="blue"
              leftIcon={<AddIcon />}
              size="sm"
            >
              New Gallery
            </Button>
          </Flex>

          {galleries.length === 0 ? (
            <Alert status="info">
              <AlertIcon />
              No galleries found. Create your first gallery!
            </Alert>
          ) : (
            <VStack spacing={4}>
              {galleries.map((gallery) => (
                <Box
                  key={gallery.id}
                  p={4}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  w="100%"
                >
                  <VStack align="stretch" spacing={3}>
                    <Flex justify="space-between" align="start">
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold" fontSize="lg">{gallery.title}</Text>
                        <Text color="gray.600" fontSize="sm">{gallery.client_name}</Text>
                      </VStack>
                      {getGalleryStatus(gallery)}
                    </Flex>
                    
                    <HStack spacing={2} fontSize="sm" color="gray.600">
                      <Text>€{gallery.price_per_image} (gallery access)</Text>
                      <Text>•</Text>
                      <Text>{formatDate(gallery.created_at)}</Text>
                    </HStack>

                    <VStack align="stretch" spacing={2}>
                      <HStack spacing={2}>
                        <Link
                          href={getClientGalleryUrl(gallery.token)}
                          isExternal
                          color="blue.500"
                          fontSize="sm"
                        >
                          View Gallery <ExternalLinkIcon mx="2px" />
                        </Link>
                        <IconButton
                          as={RouterLink}
                          to={`/admin/gallery/${gallery.id}`}
                          aria-label="Edit gallery"
                          icon={<EditIcon />}
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                        />
                        <IconButton
                          aria-label="Delete gallery"
                          icon={<DeleteIcon />}
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleDeleteClick(gallery)}
                          isLoading={deletingId === gallery.id}
                        />
                      </HStack>
                      
                      {/* Gallery Purchases */}
                      {galleryPurchases[gallery.id] && galleryPurchases[gallery.id].length > 0 && (
                        <VStack align="stretch" spacing={1}>
                          <Text fontSize="xs" color="gray.600" fontWeight="bold">
                            Purchases ({galleryPurchases[gallery.id].length}):
                          </Text>
                          {galleryPurchases[gallery.id].map((purchase: any, index: number) => (
                            <Text key={index} fontSize="xs" color="green.600">
                              {purchase.clientEmail} - €{purchase.amount} ({purchase.status})
                            </Text>
                          ))}
                        </VStack>
                      )}
                    </VStack>
                  </VStack>
                </Box>
              ))}
            </VStack>
          )}
        </VStack>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Delete Gallery</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>
                Are you sure you want to delete "{galleryToDelete?.title}"? 
                This action cannot be undone and will remove all images and purchase records.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={handleDeleteConfirm}
                isLoading={deletingId === galleryToDelete?.id}
              >
                Delete
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    );
  }

    // Desktop table layout
    return (
      <Box maxW="container.xl" mx="auto" p={8} paddingTop={{ base: "80px", md: "20px" }}>
        <VStack spacing={6} align="stretch">
          <Flex justify="space-between" align="center">
            <VStack align="start" spacing={1}>
              <Text fontSize="3xl" fontWeight="bold">
                Admin Galleries ({galleries.length})
              </Text>
              <Text fontSize="sm" color="gray.600">
                <RouterLink to="/admin" style={{ color: 'inherit' }}>← Back to Dashboard</RouterLink>
              </Text>
            </VStack>
            <Button
              as={RouterLink}
              to="/admin/gallery"
              colorScheme="blue"
              leftIcon={<AddIcon />}
            >
              Create New Gallery
            </Button>
          </Flex>

          {galleries.length === 0 ? (
            <Alert status="info">
              <AlertIcon />
              No galleries found. Create your first gallery to get started!
            </Alert>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Gallery</Th>
                    <Th>Client</Th>
                    <Th>Price</Th>
                    <Th>Status</Th>
                    <Th>Purchases</Th>
                    <Th>Created</Th>
                    <Th>Expires</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {galleries.map((gallery) => (
                    <Tr key={gallery.id}>
                      <Td>
                        <Text fontWeight="medium">{gallery.title}</Text>
                      </Td>
                      <Td>
                        <Text>{gallery.client_name}</Text>
                      </Td>
                      <Td>
                        <Text>€{gallery.price_per_image}</Text>
                        <Text fontSize="xs" color="gray.500">gallery access</Text>
                      </Td>
                      <Td>
                        {getGalleryStatus(gallery)}
                      </Td>
                      <Td>
                        {galleryPurchases[gallery.id] && galleryPurchases[gallery.id].length > 0 ? (
                          <VStack align="start" spacing={1}>
                            {galleryPurchases[gallery.id].map((purchase: any, index: number) => (
                              <Text
                                key={index}
                                fontSize="xs"
                                color="green.600"
                              >
                                {purchase.clientEmail.split('@')[0]} - €{purchase.amount} ({purchase.status})
                              </Text>
                            ))}
                          </VStack>
                        ) : (
                          <Text fontSize="sm" color="gray.400">None</Text>
                        )}
                      </Td>
                      <Td>
                        <Text fontSize="sm">{formatDate(gallery.created_at)}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm">
                          {gallery.expires_at ? formatDate(gallery.expires_at) : 'Never'}
                        </Text>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Link
                            href={getClientGalleryUrl(gallery.token)}
                            isExternal
                            color="blue.500"
                          >
                            <Button size="sm" variant="ghost" colorScheme="blue">
                              View <ExternalLinkIcon ml={1} />
                            </Button>
                          </Link>
                          <IconButton
                            as={RouterLink}
                            to={`/admin/gallery/${gallery.id}`}
                            aria-label="Edit gallery"
                            icon={<EditIcon />}
                            size="sm"
                            colorScheme="blue"
                            variant="ghost"
                          />
                          <IconButton
                            aria-label="Delete gallery"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => handleDeleteClick(gallery)}
                            isLoading={deletingId === gallery.id}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </VStack>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Delete Gallery</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>
                Are you sure you want to delete "{galleryToDelete?.title}"? 
                This action cannot be undone and will remove all images and purchase records.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={handleDeleteConfirm}
                isLoading={deletingId === galleryToDelete?.id}
              >
                Delete
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    );
  };

  return (
    <AdminAuth title="Gallery Management" description="Enter the admin password to access gallery management">
      {renderContent()}
    </AdminAuth>
  );
};

export default AdminGalleries;